import React, { useMemo, useRef, useState } from 'react'
import { apiJson, ApiError } from '../../lib/api'
import { fileTask, useTasks } from '../../components/TaskCentre'
import { useLayer } from '../../components/layers'
import { PendingButton, Failed } from '../../components/pending'
import { plural } from '../wages/format'
import { XLSX_TYPE } from '../batches/xlsx'
import { MapCanvas, MapShape } from './MapCanvas'
import { tileSource } from './mapTiles'
import { LatLng, openingView } from './mapEdit'
import { templateFileName, templateWorkbook } from './importTemplate'
import { Structure, areaText, liveVarieties, pathOf, previewLine } from './structure'
import pj from '../PickingJobsPage.module.css'
import sp from '../SitesPage.module.css'
import o from '../../components/Overlay.module.css'
import s from './ImportDialog.module.css'

// The Site Excel import (handoff §7g "Excel import"): download the workbook
// generated for this Site, fill it in, upload it, read the preview, import.
// Nothing here judges a line: the server reads the workbook on upload (the
// preview) and again at import, and the import sends back the counts it was
// shown, so a Site that changed in between is refused with the new preview
// rather than imported on the old one. The bytes are sent again at import;
// the server keeps nothing between the two.

export const MAX_FILE_BYTES = 512 * 1024

export interface ImportCounts {
  lines: number
  rows: number
  level1: number
  level2: number
  containers: number
  present: number
  held: number
  generated_ids: number
  boundaries: number
  boundaries_held: number
}

export interface ImportLine {
  row: number
  path: string[]
  location_id: string | null
  exact_variety: string
  plant_count: number | null
  status: 'create' | 'present' | 'held'
  reasons: string[]
}

export interface ImportBoundary {
  rows: number[]
  path: string[]
  status: 'create' | 'held'
  reasons: string[]
  points?: LatLng[]
  area_m2?: number
}

export interface ImportPreview {
  site: { id: string; name: string; labels: string[]; map_level: number }
  ignored_columns: Record<string, string[]>
  lines: ImportLine[]
  boundaries: ImportBoundary[]
  counts: ImportCounts
}

const lower = (x: string) => x.toLowerCase()
const pathText = (p: string[]) => p.filter(Boolean).join(' / ') || '(no path)'
const err = (e: any, fallback: string) => (e?.message as string) || fallback
const SHOWN = 200

// The server's sentence for the same counts (importSummary), for the button.
export function importSummary(c: ImportCounts, labels: string[]): string {
  const bits = [plural(c.rows, lower(labels[2]), `${lower(labels[2])}s`)]
  if (c.level1) bits.push(plural(c.level1, `new ${lower(labels[0])}`, `new ${lower(labels[0])}s`))
  if (c.level2) bits.push(plural(c.level2, `new ${lower(labels[1])}`, `new ${lower(labels[1])}s`))
  if (c.boundaries) bits.push(plural(c.boundaries, 'boundary', 'boundaries'))
  return bits.join(', ')
}

export const rowsText = (rows: number[]) => (rows.length === 1 ? `row ${rows[0]}` : `rows ${previewLine(rows.map(String))}`)

export function toBase64(bytes: Uint8Array): string {
  let bin = ''
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return btoa(bin)
}

async function fileBytes(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') return new Uint8Array(await file.arrayBuffer())
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(new Uint8Array(r.result as ArrayBuffer))
    r.onerror = () => reject(r.error)
    r.readAsArrayBuffer(file)
  })
}

const tiles = tileSource()

export function ImportDialog({ user, data, onClose, onDone }: { user: any; data: Structure; onClose: () => void; onDone: (message: string) => Promise<void> | void }) {
  const { site } = data
  const labels = [site.level1_label, site.level2_label, site.level3_label]
  const [file, setFile] = useState<{ name: string; data: string } | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const { startTask } = useTasks()
  const noVarieties = !liveVarieties(data.varieties).length
  // A layer (h 425-458): Escape, the scrim and Cancel close it. A workbook
  // read but not imported is unsaved, so closing then asks first. The import
  // button's write is guarded (h 3978-3982).
  const dialogEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: !!file && !busy, el: dialogEl, modal: true, busy })

  // The template is a file, so it goes to the task centre (§6d). The dialog
  // stays open: filling the template in and choosing it is the next step here.
  const template = () =>
    startTask(fileTask('Import template', site.name, templateFileName(site), XLSX_TYPE, () => templateWorkbook(site, data.varieties, new Date())))

  const choose = async (f: File | undefined) => {
    setPreview(null)
    setFile(null)
    setError('')
    if (!f) return
    if (f.size > MAX_FILE_BYTES) {
      setError(`This workbook is larger than ${MAX_FILE_BYTES / 1024} KB. Import the Site in parts.`)
      return
    }
    setBusy(true)
    try {
      const picked = { name: f.name, data: toBase64(await fileBytes(f)) }
      const r = await apiJson<{ preview: ImportPreview }>(user, `/api/sites/${site.id}/structure/import`, { method: 'POST', body: { file_name: picked.name, data: picked.data } })
      setFile(picked)
      setPreview(r.preview)
    } catch (e: any) {
      setError(err(e, 'The workbook could not be read'))
    } finally {
      setBusy(false)
    }
  }

  const submit = async () => {
    if (busy || !file || !preview) return
    const c = preview.counts
    setBusy(true)
    setError('')
    try {
      const r = await apiJson<{ summary: string }>(user, `/api/sites/${site.id}/structure/import`, {
        method: 'POST',
        body: { file_name: file.name, data: file.data, commit: true, expect: { rows: c.rows, containers: c.containers, boundaries: c.boundaries } },
      })
      await onDone(`Imported ${r.summary}`)
    } catch (e: any) {
      // A change since the preview (or nothing left to import) comes back
      // with the preview as it now reads.
      if (e instanceof ApiError && e.body?.preview) setPreview(e.body.preview)
      setError(err(e, 'Nothing was imported'))
    } finally {
      setBusy(false)
    }
  }

  const c = preview?.counts
  const nothing = !!c && !c.rows && !c.boundaries
  const blocked = !preview ? (busy ? '' : 'Choose a workbook first') : nothing ? 'Nothing in this workbook can be imported' : ''

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={dialogEl} className={`${o.modal} ${o.wide}`} role="dialog" aria-label="Import" onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Import into {site.name}</h2>
            <p className={o.sub}>
              Adds {lower(labels[2])}s and boundaries from a workbook. It never changes what the Site already has, and a line with a problem is held back whole. Download the workbook made for this Site, fill in its Locations sheet (and Boundaries, if you have them), then upload it to see what it would do.
            </p>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          {noVarieties && (
            <div className={o.danger}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01" />
              </svg>
              <span>This Site has no varieties on its list yet, so every line would be held back. Add them under Varieties first, then download the workbook.</span>
            </div>
          )}
          <div className={s.steps}>
            <button type="button" className={o.ghost} onClick={template}>
              Download the workbook
            </button>
            <label className={`${o.ghost} ${s.upload} ${busy ? s.uploadOff : ''}`} htmlFor="imp-file">
              Upload the filled-in workbook
            </label>
            <input
              id="imp-file"
              className={s.fileInput}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={busy}
              onChange={(e) => choose(e.target.files?.[0])}
            />
            {busy && !preview ? <span className={s.file}>Reading…</span> : file && !preview && <span className={s.file}>{file.name}</span>}
          </div>

          {preview && <PreviewBody preview={preview} data={data} fileName={file?.name || ''} />}
        </div>

        <div className={o.foot}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          {blocked && <span className={o.footNote}>{blocked}</span>}
          <PendingButton
            className={o.primary}
            busy={busy}
            pending="Importing…"
            disabled={!!blocked || !preview}
            onClick={submit}
          >
            {preview && !nothing ? `Import ${importSummary(preview.counts, preview.site.labels)}` : 'Import'}
          </PendingButton>
        </div>
        <Failed text={error} />
      </div>
      {confirm}
    </div>
  )
}

function PreviewBody({ preview, data, fileName }: { preview: ImportPreview; data: Structure; fileName: string }) {
  const { counts: c, site } = preview
  const labels = site.labels
  const adds = preview.lines.filter((l) => l.status === 'create')
  const present = preview.lines.filter((l) => l.status === 'present')
  const held = [
    ...preview.lines.filter((l) => l.status === 'held').map((l) => ({ sheet: 'Locations', rows: [l.row], path: l.path, reasons: l.reasons })),
    ...preview.boundaries.filter((b) => b.status === 'held').map((b) => ({ sheet: 'Boundaries', rows: b.rows, path: b.path, reasons: b.reasons })),
  ].sort((a, b) => (a.sheet === b.sheet ? a.rows[0] - b.rows[0] : a.sheet === 'Locations' ? -1 : 1))
  const ignored = Object.entries(preview.ignored_columns || {}).filter(([, cols]) => cols.length)
  const newShapes = preview.boundaries.filter((b) => b.status === 'create' && b.points)

  return (
    <>
      <div className={s.file}>
        {fileName}: {plural(c.lines, 'line', 'lines')} on the Locations sheet
        {preview.boundaries.length ? `, ${plural(preview.boundaries.reduce((n, b) => n + b.rows.length, 0), 'point', 'points')} on the Boundaries sheet` : ''}
      </div>
      <div className={s.counts} data-testid="import-counts">
        <div className={s.count}>
          <span className={s.countHead}>Will be added</span>
          <span className={s.countMain}>{plural(c.rows, lower(labels[2]), `${lower(labels[2])}s`)}</span>
          <span className={s.countNote}>
            {[
              c.level1 ? plural(c.level1, `new ${lower(labels[0])}`, `new ${lower(labels[0])}s`) : '',
              c.level2 ? plural(c.level2, `new ${lower(labels[1])}`, `new ${lower(labels[1])}s`) : '',
              plural(c.boundaries, 'boundary', 'boundaries'),
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {c.generated_ids > 0 && <span className={s.countNote}>{plural(c.generated_ids, 'Location ID', 'Location IDs')} to be generated</span>}
        </div>
        <div className={s.count}>
          <span className={s.countHead}>Already on the Site</span>
          <span className={s.countMain}>{plural(c.present, 'line', 'lines')}</span>
          <span className={s.countNote}>Skipped: nothing is added twice</span>
        </div>
        <div className={`${s.count} ${c.held || c.boundaries_held ? s.countHeld : ''}`}>
          <span className={s.countHead}>Held back</span>
          <span className={s.countMain}>{plural(c.held, 'line', 'lines')}</span>
          <span className={s.countNote}>{plural(c.boundaries_held, 'boundary', 'boundaries')} · nothing is written for them</span>
        </div>
      </div>

      {ignored.length > 0 && (
        <p className={pj.note}>
          Not read: {ignored.map(([sheet, cols]) => `${cols.join(', ')} on the ${sheet} sheet`).join('; ')}.
        </p>
      )}

      {held.length > 0 && (
        <div className={s.section}>
          <div className={s.sectionHead}>Held back: correct these in the workbook and upload it again</div>
          <ul className={s.held} data-testid="import-held">
            {held.slice(0, SHOWN).map((h) => (
              <li key={`${h.sheet}-${h.rows[0]}`} className={s.heldItem}>
                <span className={s.heldWhere}>
                  {h.sheet} {rowsText(h.rows)}
                </span>{' '}
                · {pathText(h.path)}
                <ul className={s.heldReasons}>
                  {h.reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          {held.length > SHOWN && <p className={pj.note}>and {held.length - SHOWN} more</p>}
        </div>
      )}

      {adds.length > 0 && (
        <div className={s.section}>
          <div className={s.sectionHead}>To be added</div>
          <div className={s.scroll}>
            <table className={s.table} data-testid="import-adds">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>{labels.join(' / ')}</th>
                  <th>Location ID</th>
                  <th>Variety</th>
                  <th className={s.num}>Plants</th>
                </tr>
              </thead>
              <tbody>
                {adds.slice(0, SHOWN).map((l) => (
                  <tr key={l.row}>
                    <td>{l.row}</td>
                    <td>{pathText(l.path)}</td>
                    <td>{l.location_id || <span className={s.generated}>generated</span>}</td>
                    <td>{l.exact_variety}</td>
                    <td className={s.num}>{l.plant_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {adds.length > SHOWN && <p className={pj.note}>and {adds.length - SHOWN} more</p>}
        </div>
      )}

      {present.length > 0 && (
        <p className={pj.note}>
          Already on the Site, skipped: spreadsheet {rowsText(present.map((l) => l.row))}.
        </p>
      )}

      {newShapes.length > 0 && <PreviewMap data={data} shapes={newShapes} />}
    </>
  )
}

// The boundaries the import would add, drawn on the imagery with the ones
// the Site already has.
function PreviewMap({ data, shapes }: { data: Structure; shapes: ImportBoundary[] }) {
  const byId = useMemo(() => new Map(data.nodes.map((n) => [n.id, n])), [data.nodes])
  const all: MapShape[] = useMemo(
    () => [
      ...data.boundaries.map((b) => ({ id: b.id, nodeId: b.node_id, label: pathOf(byId, byId.get(b.node_id)), points: b.points })),
      ...shapes.map((b, i) => ({ id: `new-${i}`, nodeId: `new-${i}`, label: `New: ${pathText(b.path)} (${areaText(b.area_m2)})`, points: b.points || [], tone: 'new' as const })),
    ],
    [data.boundaries, shapes, byId]
  )
  const fit = shapes.flatMap((b) => b.points || [])
  const opening = fit.length ? { kind: 'fit' as const, points: fit } : openingView(data.site, data.boundaries)
  const none = () => {}
  return (
    <div className={s.section}>
      <div className={s.sectionHead}>Boundaries to be added</div>
      <div className={s.legend}>
        <span>
          <span className={`${s.swatch} ${s.swatchNew}`} />
          New ({shapes.length})
        </span>
        <span>
          <span className={`${s.swatch} ${s.swatchLive}`} />
          Already on the Site ({data.boundaries.length})
        </span>
      </div>
      <div className={s.mapBox}>
        <MapCanvas tiles={tiles} opening={opening} shapes={all} selected={null} edit={null} zoomTo={null} onSelect={none} onAdd={none} onMove={none} onInsert={none} onDelete={none} />
      </div>
    </div>
  )
}
